<template>
  <section class="[ icon-catalog-picker ] rounded-md border border-white/15 bg-white/5 px-3 py-3">
    <div class="mb-2 flex items-center justify-between gap-2">
      <p class="text-xs font-bold uppercase tracking-wide text-pink-200">{{ label }}</p>
      <div class="text-xs text-pink-300">
        {{ selectedLabel }}
      </div>
    </div>

    <input
      v-model="searchQuery"
      type="text"
      class="w-full rounded-md border border-pink-700/60 bg-white px-3 py-2 text-sm text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300/70"
      :placeholder="searchPlaceholder"
    >

    <div v-if="filteredIcons.length" class="mt-2 grid grid-cols-5 gap-2 sm:grid-cols-6">
      <button
        v-for="iconEntry in filteredIcons"
        :key="`icon-picker-${iconEntry.iconKey}`"
        type="button"
        class="flex h-10 items-center justify-center rounded-md border text-pink-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300/70"
        :class="modelValue === iconEntry.iconKey ? 'border-pink-200/90 bg-pink-700/35' : 'border-white/20 bg-white/5 hover:bg-pink-700/25'"
        :title="iconEntry.label"
        @click="$emit('update:modelValue', iconEntry.iconKey)"
      >
        <Icon :icon="iconEntry.iconKey" class="h-5 w-5" />
      </button>
    </div>

    <p v-else class="mt-2 text-xs text-pink-300/80">
      {{ emptyMessage }}
    </p>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Icon } from '@iconify/vue';

defineOptions({ name: 'IconCatalogPicker' });

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  icons: {
    type: Array,
    default: () => []
  },
  label: {
    type: String,
    default: 'Select Icon'
  },
  searchPlaceholder: {
    type: String,
    default: 'Search icons'
  },
  emptyMessage: {
    type: String,
    default: 'No icons available for the current search.'
  }
});

defineEmits(['update:modelValue']);

const searchQuery = ref('');

const normalizedIcons = computed(() => {
  const rows = (Array.isArray(props.icons) ? props.icons : [])
    .map((iconEntry) => ({
      iconKey: String(iconEntry?.iconKey || '').trim(),
      label: String(iconEntry?.label || iconEntry?.iconKey || '').trim(),
      category: String(iconEntry?.category || '').trim(),
      library: String(iconEntry?.library || '').trim(),
      keywords: Array.isArray(iconEntry?.keywords) ? iconEntry.keywords.map((entry) => String(entry || '').trim()) : []
    }))
    .filter((iconEntry) => iconEntry.iconKey);

  const currentKey = String(props.modelValue || '').trim();
  if (!currentKey || rows.some((iconEntry) => iconEntry.iconKey === currentKey)) {
    return rows;
  }

  return [
    {
      iconKey: currentKey,
      label: currentKey,
      category: '',
      library: '',
      keywords: []
    },
    ...rows
  ];
});

const filteredIcons = computed(() => {
  const query = String(searchQuery.value || '').trim().toLowerCase();
  if (!query) return normalizedIcons.value;

  return normalizedIcons.value.filter((iconEntry) => {
    const haystack = [
      iconEntry.label,
      iconEntry.iconKey,
      iconEntry.category,
      iconEntry.library,
      ...iconEntry.keywords
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  });
});

const selectedLabel = computed(() => {
  const currentKey = String(props.modelValue || '').trim();
  const match = normalizedIcons.value.find((iconEntry) => iconEntry.iconKey === currentKey);
  return match?.label || 'No icon selected';
});
</script>
